import hotkeys from 'hotkeys-js';
import { HotKeys, SectionType } from '@/typings/enums/enums';
import { useStore } from '@/store';
import RowModel from '@/components/layout/row/RowModel';
import AddonModel from '@/models/grid/AddonModel';
import { SectionBaseModel } from '@/components/layout/section/SectionBaseModel';
import { ref, SetupContext } from 'vue';
import { modalButtonId } from '@/typings/types/types';
import ColumnModel from '@/components/layout/column/ColumnModel';
import { AddonRegistrationModel } from '@/components/addons/registration/Model';
import { ModalButton } from '@/components/components/editing/Modals/interfaces';
import useFlow from '@/hooks/useFlow';

export default function useEditOptions(context: SetupContext) {
  const store = useStore();
  const showPrompt = ref(false);
  const promptHeading = ref<string | undefined>();
  const promptDescription = ref<string | undefined>();
  const promptButtons = ref<ModalButton[]>([]);

  const onHide = () => {
    showPrompt.value = false;
    /**
     * Dont save block if we are on a section, as this is handled in the campaignModel itself.
     */
    store.dispatch('editing/hideContextMenu');
    context.emit('closeContextMenu');
  };

  const onHideAndSave = () => {
    const model = store.getters['editing/activeModel'];
    if (model && !(model instanceof SectionBaseModel)) {
      model.saveBlock();
    }
    store.dispatch('editing/hideContextMenu');
    context.emit('closeContextMenu');
  };

  const handlePromptButtonClicked = async (id: modalButtonId) => {
    const model = store.getters['editing/activeModel'];
    const { makeSpecificFlowPageActive } = useFlow();
    const campaignModel = store.getters['campaign/model'];
    if (model && id === 'yes') {
      const currentIndex = model.index;
      let sectionType: SectionType | undefined;
      if (model instanceof SectionBaseModel) {
        sectionType = model.getSectionType();
      }

      await model.delete();
      await store.dispatch('editing/removeActiveModel');

      /**
       * Make first section active
       */
      if (model instanceof SectionBaseModel) {
        let firstElement;
        if (sectionType === SectionType.SECTION) {
          firstElement = campaignModel.getFirstSection();
        }
        if (sectionType === SectionType.FLOWPAGE) {
          firstElement = campaignModel.getFirstFlowPage();
        }
        if (sectionType === SectionType.POPOVER) {
          firstElement = campaignModel.getFirstPopover();
        }
        store.commit('editing/setActiveModel', {
          model: firstElement
        });
        if (sectionType === SectionType.FLOWPAGE && firstElement) {
          await makeSpecificFlowPageActive(firstElement.state.id);
        }
      }

      /**
       * Make either parent row, next row or parent section active
       */
      if (model instanceof RowModel) {
        let row = model.section.getRowByIndex(currentIndex - 1);
        if (!row) {
          row = model.section.getRowByIndex(currentIndex + 1);
        }
        if (row) {
          store.commit('editing/setActiveModel', {
            model: row
          });
        } else {
          store.commit('editing/setActiveModel', {
            model: model.section
          });
        }
      }
      /**
       * Make either parent column, next column or parent row active
       */
      if (model instanceof ColumnModel) {
        let column = model.row.getColumnByIndex(currentIndex - 1);
        if (!column) {
          column = model.row.getColumnByIndex(currentIndex + 1);
        }
        if (column) {
          store.commit('editing/setActiveModel', {
            model: column
          });
        } else {
          store.commit('editing/setActiveModel', {
            model: model.row
          });
        }
      }
      /**
       * Make either parent addon, next addon or parent row column
       */
      if (model instanceof AddonModel) {
        let addon = model.column.getAddonByIndex(currentIndex - 1);
        if (!addon) {
          addon = model.column.getAddonByIndex(currentIndex + 1);
        }
        if (addon) {
          store.commit('editing/setActiveModel', {
            model: addon
          });
        } else {
          store.commit('editing/setActiveModel', {
            model: model.column
          });
        }
      }
      showPrompt.value = false;
      onHide();
    }
    if (id === 'no') {
      showPrompt.value = false;
      onHide();
    }
    if (id === 'unlink') {
      if (model instanceof SectionBaseModel) {
        await model.onUnlink();
      }
      showPrompt.value = false;
    }
    onHideAndSave();
  };

  const onOpenGameSettings = (e: Event) => {
    e.preventDefault();

    store.commit('editing/setShowGameSettingsModal', true);
    onHide();
  };

  const onOpenLayoutSettings = (e: Event) => {
    e.preventDefault();

    store.commit('editing/setShowLayoutSettingsModal', true);
    onHide();
  };

  const onOpenSectionSettings = (e: Event) => {
    e.preventDefault();
    const model = store.getters['editing/activeModel'];
    if (model instanceof SectionBaseModel || model instanceof AddonModel) {
      model.openSectionSettings();
    }
    onHide();
  };

  const onMoveUp = (e: Event) => {
    e.preventDefault();
    const model = store.getters['editing/activeModel'];
    if (model) {
      model.onMoveUp();
    }
    onHideAndSave();
  };

  const onMoveDown = (e: Event) => {
    e.preventDefault();
    const model = store.getters['editing/activeModel'];
    if (model) {
      model.onMoveDown();
    }
    onHideAndSave();
  };

  const handleInsertExtraColumn = (e: Event) => {
    e.preventDefault();
    const model = store.getters['editing/activeModel'];
    if (model instanceof RowModel) {
      model.handleInsertExtraColumn();
    } else if (model instanceof AddonModel) {
      model.column.row.handleInsertExtraColumn();
    } else if (model instanceof ColumnModel) {
      model.row.handleInsertExtraColumn();
    }
    onHide(); //Saving is done within onInsertExtraColumn method
  };

  const onCopy = (e: Event) => {
    e.preventDefault();
    const model = store.getters['editing/activeModel'];
    store.dispatch('clipboard/addToClipboard', model);
    onHide();
  };

  const onDuplicate = (e: Event) => {
    e.preventDefault();
    const model = store.getters['editing/activeModel'];
    store.dispatch('clipboard/addToClipboard', model);
    if (model) {
      if (model.canPaste()) {
        model.onPasteElement();
      }
    }
    store.dispatch('clipboard/onResetClipboard');
    onHide();
  };

  const onPaste = (e: Event) => {
    e.preventDefault();
    const model = store.getters['editing/activeModel'];
    if (model) {
      if (model.canPaste()) {
        model.onPasteElement();
      }
    }
    onHideAndSave();
  };

  const onDelete = (e: Event) => {
    e.preventDefault();
    const model = store.getters['editing/activeModel'];
    const campaignModel = store.getters['campaign/model'];

    let msg: string | undefined;

    let hasRegistrationAddon = false;

    if (model instanceof SectionBaseModel || model instanceof RowModel || model instanceof ColumnModel) {
      hasRegistrationAddon = model.getAddons<AddonRegistrationModel>('registration').length > 0;
    } else if (model && model.alias === 'registration') {
      hasRegistrationAddon = true;
    }

    if (hasRegistrationAddon && campaignModel.state?.config?.hasRegistrations) {
      if (model instanceof SectionBaseModel)
        msg =
          'This campaign already has registrations therefore you are not able to delete this block because it include a registration form.';
      if (model instanceof RowModel)
        msg =
          'This campaign already has registrations therefore you are not able to delete this row because it include a registration form.';
      if (model instanceof ColumnModel)
        msg =
          'This campaign already has registrations therefore you are not able to delete this column because it include a registration form.';
      if (model instanceof AddonModel)
        msg = 'This campaign already has registrations therefore you are not able to delete the registration form.';

      if (msg) {
        promptDescription.value = msg;
      }
      promptButtons.value = [
        {
          id: 'no',
          variant: 'text',
          label: 'Close'
        }
      ];

      showPrompt.value = true;
      return;
    }
    if (model instanceof SectionBaseModel) msg = 'Are you sure you wish to delete this section?';
    if (model instanceof RowModel) msg = 'Are you sure you wish to delete this row?';
    if (model instanceof ColumnModel) msg = 'Are you sure you wish to delete this column?';
    if (model instanceof AddonModel) msg = 'Are you sure you wish to delete this addon?';

    if (msg) {
      promptDescription.value = msg;
    }
    promptButtons.value = [
      {
        id: 'yes',
        variant: 'text',
        label: 'Yes, delete'
      },
      {
        id: 'no',
        variant: 'text',
        label: 'No, cancel'
      }
    ];

    showPrompt.value = true;
  };

  const unbindHotkeys = () => {
    hotkeys.unbind(HotKeys.COPY, onCopy);

    // Paste
    hotkeys.unbind(HotKeys.PASTE, onPaste);

    // Move up
    hotkeys.unbind(HotKeys.MOVEUP, onMoveUp);

    // Move down
    hotkeys.unbind(HotKeys.MOVEDOWN, onMoveDown);

    // Section settings
    hotkeys.unbind(HotKeys.SECTION_SETTINGS, onOpenSectionSettings);

    // Duplicate
    hotkeys.unbind(HotKeys.DUPLICATE, onDuplicate);

    //Delete
    hotkeys.unbind(HotKeys.DELETE, onDelete);

    //Insert column
    hotkeys.unbind(HotKeys.EXTRA_COLUMN, handleInsertExtraColumn);

    //Game Settings
    hotkeys.unbind(HotKeys.GAME_SETTINGS, onOpenGameSettings);

    //Layout Settings
    hotkeys.unbind(HotKeys.LAYOUT_SETTINGS, onOpenLayoutSettings);
  };
  const initHotKeys = () => {
    //Copy
    hotkeys(HotKeys.COPY, onCopy);

    // Paste
    hotkeys(HotKeys.PASTE, onPaste);

    // Move up
    hotkeys(HotKeys.MOVEUP, onMoveUp);

    // Move down
    hotkeys(HotKeys.MOVEDOWN, onMoveDown);

    // Section settings
    hotkeys(HotKeys.SECTION_SETTINGS, onOpenSectionSettings);

    // Duplicate
    hotkeys(HotKeys.DUPLICATE, onDuplicate);

    //Delete
    hotkeys(HotKeys.DELETE, onDelete);

    //Insert column
    hotkeys(HotKeys.EXTRA_COLUMN, handleInsertExtraColumn);

    //Game Settings
    hotkeys(HotKeys.GAME_SETTINGS, onOpenGameSettings);

    //Layout Settings
    hotkeys(HotKeys.LAYOUT_SETTINGS, onOpenLayoutSettings);
  };

  return {
    initHotKeys,
    unbindHotkeys,
    showPrompt,
    promptHeading,
    promptDescription,
    promptButtons,
    handlePromptButtonClicked
  };
}
